import React, { useEffect, useRef, useState } from "react";
import { Button, Caption1, makeStyles, Spinner, Subtitle2 } from "@fluentui/react-components";
import { useAddInStore, useTokenStore } from "../../../hooks";
import {
  OnboardingBroadcastMessage,
  startCadChatOnboardingProcess,
  supabaseApi,
} from "../../../services";
import { envUtils } from "../../../utils/envUtils";
import { logger } from "../../../utils";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    overflowY: "hidden",
  },
  body: {
    display: "flex",
    flexDirection: "column",
    padding: "20px",
    gap: "5px",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  actions: {
    display: "flex",
    flexDirection: "row",
    gap: "5px",
    marginTop: "10px",
    marginBottom: "10px",
    alignContent: "center",
    alignItems: "center",
    justifyContent: "center",
    "> button": {
      width: "100%",
    },
  },
});

const Onboarding = () => {
  const styles = useStyles();

  // indicated the current onboarding button
  const buttonRef = useRef<HTMLButtonElement>(null);

  // indicates the current open dialog
  const dialog = useRef<Office.Dialog | undefined>(undefined);

  // indicates the current user is authenticated
  const isAuthenticated = useTokenStore((state) => state.isAuthenticated);

  // indicates the selected space id
  const setCurrentView = useAddInStore((state) => state.setCurrentView);

  // used to set the current dialog state
  const setIsDialogOpen = useAddInStore((state) => state.setIsDialogOpen);

  // used to set the current dialog state
  const isDialogOpen = useAddInStore((state) => state.isDialogOpen);

  // indicates the current organization id
  const organizationCount = useAddInStore((state) => state.organizations)?.length ?? 0;

  // indicates the current organization id
  const selectedOrganizationId = useAddInStore((state) => state.selectedOrganizationId);

  // indicates that the user has start the onboarding process
  const [isOnboarding, setIsOnboarding] = useState<boolean>(false);

  // indicates that the user can create space in the selected organization
  const [onboardingRedirectUrl, setOnboardingRedirectUrl] = useState<string | undefined>(undefined);

  // indicates the current status of the users onboarding progress
  const onboardingProgress = useAddInStore((state) => state.onboardingProgress);

  /**
   * triggers the closing of the onboarding dialog
   *
   * @returns {void}
   */
  const handleCloseDialog = () => {
    if (!dialog.current) return;

    logger.info("Closing onboarding dialog");

    // close the dialog and reset state value
    dialog.current.close();
    dialog.current = undefined;

    // reset the dialog state
    setIsDialogOpen(false);

    // reset the onboarding state
    setIsOnboarding(false);
  };

  useEffect(() => {
    if (!buttonRef.current || !onboardingProgress.initialized || !isAuthenticated || isDialogOpen)
      return;

    const checkPermissions = async () => {
      try {
        // if onboarding is complete, we don't need to check permissions
        // also if the user has completed the plan onboarding step then
        // they will be able to create spaces once user profile is complete
        if (onboardingProgress.complete) {
          handleCloseDialog();
          setCurrentView(selectedOrganizationId ? "default" : "settings");
          return;
        } else if (onboardingProgress.plansComplete) {
          setOnboardingRedirectUrl(
            [
              envUtils.CADCHAT_APP_BASE_URL,
              "/onboarding",
              "/user_profile_bypass_device_permissions",
            ].join("")
          );
          return;
        }

        const hasPermission = await supabaseApi.hasPermissionInAnyOrganization("space.INSERT");
        setOnboardingRedirectUrl(
          [
            envUtils.CADCHAT_APP_BASE_URL,
            "/onboarding",
            hasPermission && organizationCount > 0
              ? "/user_profile_bypass_device_permissions"
              : "/plans?redirect_pipeline=default_bypass_device_permissions",
          ].join("")
        );
      } catch (error) {
        logger.error("Error checking permissions", error);
      }
    };

    checkPermissions();
  }, [
    isAuthenticated,
    selectedOrganizationId,
    onboardingProgress,
    organizationCount,
    isDialogOpen,
  ]);

  useEffect(() => {
    supabaseApi.connectToOnboardingBroadcast((payload: OnboardingBroadcastMessage) => {
      logger.debug("Onboarding broadcast received:", payload);

      if (payload?.windowClosed || payload?.isComplete) {
        handleCloseDialog();
      }
    });

    return () => {
      supabaseApi.disconnectFromOnboardingBroadcast();
    };
  }, []);

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        <div className={styles.content}>
          <Subtitle2>Thank you for installing the CADchat Add-In</Subtitle2>
          <Caption1>
            It looks like you haven't finished onboarding. You need to complete our short onboarding
            process before you are able to use this add-in. Once completed you'll be able to start
            creating CADchat meetings right in your Outlook calendar.
          </Caption1>
        </div>

        <div className={styles.actions}>
          <Button
            appearance="primary"
            ref={buttonRef}
            disabled={!onboardingRedirectUrl}
            icon={isOnboarding ? <Spinner size="tiny" /> : undefined}
            onClick={async () => {
              try {
                if (dialog.current) {
                  dialog.current.close();
                  setIsDialogOpen(false);
                }

                setIsOnboarding(true);
                dialog.current = await startCadChatOnboardingProcess(onboardingRedirectUrl);
              } catch (error) {
                logger.error("Error starting onboarding process", error);
                setIsOnboarding(false);
              }
            }}
          >
            {isOnboarding ? "Onboarding In Progress" : "Complete Onboarding"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
